import React, { useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Phone,
  Mail,
  Clock,
  Send,
  CheckCircle2,
} from "lucide-react";
import { companyInfo } from "../data/mock";
import { useScrollReveal } from "../hooks/useScrollReveal";

const Section = ({ children, className = "" }) => {
  const [ref, isVisible] = useScrollReveal();
  return (
    <section
      ref={ref}
      className={`reveal ${isVisible ? "visible" : ""} ${className}`}
    >
      {children}
    </section>
  );
};

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      setSubmitted(true);
      toast.success(response.data.message || "Your enquiry has been submitted! We'll be in touch soon.");
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error("Something went wrong. Please try calling us directly or try again later.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="py-28 lg:py-36">
        <div className="container mx-auto px-6">
          <div className="max-w-lg mx-auto text-center page-enter">
            <div className="w-20 h-20 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-emerald-500" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-4">
              Thank You!
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              We've received your enquiry and will get back to you within 1
              business day. For urgent matters, please call us directly.
            </p>
            <Button
              onClick={() => {
                setSubmitted(false);
                setFormData({
                  name: "",
                  email: "",
                  phone: "",
                  company: "",
                  service: "",
                  message: "",
                });
              }}
              variant="outline"
              className="border-slate-300"
            >
              Submit Another Enquiry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Hero */}
      <section className="bg-slate-50 py-16 lg:py-20">
        <div className="container mx-auto px-6 page-enter">
          <div className="max-w-2xl">
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-4">
              Get In Touch
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Ready to improve your IT? Contact us for a free, no-obligation
              consultation. We'd love to hear about your business and how we can
              help.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form + Info */}
      <Section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-12 lg:gap-16">
            {/* Form */}
            <div className="lg:col-span-2">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                Send Us an Enquiry
              </h2>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-slate-700">
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      placeholder="John Smith"
                      value={formData.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                      className="h-11 border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700">
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="john@company.com.au"
                      value={formData.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      className="h-11 border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-slate-700">
                      Phone Number
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="04XX XXX XXX"
                      value={formData.phone}
                      onChange={(e) => handleChange("phone", e.target.value)}
                      className="h-11 border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-slate-700">
                      Company Name
                    </Label>
                    <Input
                      id="company"
                      placeholder="Your Business Name"
                      value={formData.company}
                      onChange={(e) => handleChange("company", e.target.value)}
                      className="h-11 border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-700">
                    What service are you interested in?
                  </Label>
                  <Select
                    value={formData.service}
                    onValueChange={(val) => handleChange("service", val)}
                  >
                    <SelectTrigger className="h-11 border-slate-200 focus:border-blue-500 focus:ring-blue-500">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="managed-it">Managed IT Support</SelectItem>
                      <SelectItem value="microsoft-365">Microsoft 365 Support</SelectItem>
                      <SelectItem value="cybersecurity">Cybersecurity</SelectItem>
                      <SelectItem value="cloud">Cloud Services</SelectItem>
                      <SelectItem value="backup">Backup & Disaster Recovery</SelectItem>
                      <SelectItem value="consulting">IT Consulting</SelectItem>
                      <SelectItem value="other">Other / Not Sure</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-slate-700">
                    Message *
                  </Label>
                  <Textarea
                    id="message"
                    placeholder="Tell us about your IT needs, current challenges, or any questions you have..."
                    value={formData.message}
                    onChange={(e) => handleChange("message", e.target.value)}
                    rows={5}
                    className="border-slate-200 focus:border-blue-500 focus:ring-blue-500 resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 text-white btn-primary h-12 px-8 text-base"
                >
                  {loading ? (
                    "Sending..."
                  ) : (
                    <>
                      Send Enquiry
                      <Send className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card className="border-slate-100">
                <CardContent className="p-7">
                  <h3 className="font-semibold text-slate-900 mb-5">
                    Contact Information
                  </h3>
                  <div className="space-y-4">
                    <a
                      href={`tel:${companyInfo.phone.replace(/\s/g, "")}`}
                      className="flex items-center gap-3 text-sm text-slate-600 hover:text-blue-600 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                        <Phone className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <div className="font-medium text-slate-900">Phone</div>
                        {companyInfo.phone}
                      </div>
                    </a>
                    <a
                      href={`mailto:${companyInfo.email}`}
                      className="flex items-center gap-3 text-sm text-slate-600 hover:text-blue-600 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                        <Mail className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <div className="font-medium text-slate-900">Email</div>
                        {companyInfo.email}
                      </div>
                    </a>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-slate-100">
                <CardContent className="p-7">
                  <h3 className="font-semibold text-slate-900 mb-3">
                    Business Hours
                  </h3>
                  <div className="space-y-2 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-blue-600" />
                      <span>Mon - Fri: 8:00 AM - 6:00 PM</span>
                    </div>
                    <p className="text-slate-500 mt-3">
                      Emergency support available 24/7 for managed service
                      clients.
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-blue-100 bg-blue-50/50">
                <CardContent className="p-7">
                  <h3 className="font-semibold text-slate-900 mb-2">
                    Free IT Assessment
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Not sure where to start? We offer a free IT assessment to
                    help identify your business's technology needs and
                    opportunities for improvement.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
